import { useState } from 'react';
import { api, uploadMedia } from '../lib/api';
import { useAction, useApi } from '../lib/useApi';
import { Recorder } from '../components/Recorder';
import { ErrorBox, Loading, Notice } from '../components/ui';
import { useFamilyTerms } from '../lib/terms';

interface Person {
  id: string;
  display_name: string;
  relation_label: string | null;
}
interface Dialect {
  code: string;
  name: string;
  english_name: string;
  region: string | null;
}

type Kind = 'story' | 'proverb' | 'oriki' | 'recipe' | 'song';

const KINDS: { key: Kind; icon: string; label: string; termKey: string; prompt: string }[] = [
  { key: 'story', icon: '📖', label: 'A story', termKey: 'type.story', prompt: 'Tell us about when you were small.' },
  { key: 'proverb', icon: '💭', label: 'A proverb', termKey: 'type.proverb', prompt: 'Say a proverb your mother used to say.' },
  { key: 'oriki', icon: '🪘', label: 'Praise poetry', termKey: 'type.oriki', prompt: 'Say the praise poetry of our family.' },
  { key: 'recipe', icon: '🍲', label: 'A recipe', termKey: 'type.recipe', prompt: 'Tell us how you cook it, step by step.' },
  { key: 'song', icon: '🎵', label: 'A song', termKey: 'type.song', prompt: 'Sing the song you sang to your children.' },
];

/**
 * Elder Mode.
 *
 * Built for a grandmother holding a phone at arm's length. One decision per
 * screen, enormous targets, no typing required at any point, and the recording
 * saves the moment she stops speaking. Everything optional stays optional:
 * a title, a dialect and a transcript can all be added later by someone else.
 */
export function ElderModePage({ elder = false }: { elder?: boolean } = {}) {
  const people = useApi<{ people: Person[] }>('/tree');
  const dialects = useApi<{ dialects: Dialect[] }>('/dialects?language=yo');
  const act = useAction();
  const { term, primary } = useFamilyTerms();

  const [kind, setKind] = useState<Kind | null>(null);
  const [personId, setPersonId] = useState('');
  const [dialectCode, setDialectCode] = useState('');
  const [saved, setSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (people.loading) return <Loading label="Getting things ready…" />;

  const chosen = KINDS.find((k) => k.key === kind);

  async function save(blob: Blob, seconds: number) {
    if (!chosen) return;
    setBusy(true);
    const stamp = new Date().toLocaleDateString(undefined, { dateStyle: 'medium' });
    const ok = await act.run(async () => {
      const mediaAssetId = await uploadMedia(blob, 'story');
      return api('/stories', {
        body: {
          title: `${chosen.label} — ${stamp}`,
          type: chosen.key,
          languageCode: 'yo',
          personId: personId || undefined,
          dialectCode: dialectCode || undefined,
          mediaAssetId,
          // Her own voice, in her own words: the strongest provenance there is.
          provenance: 'family_recording',
        },
      });
    }, 'Saved. Thank you.');
    setBusy(false);
    if (ok) {
      setSaved(`${chosen.label.toLowerCase()} · ${Math.floor(seconds / 60)}m ${seconds % 60}s`);
      setKind(null);
    }
  }

  return (
    <div className={elder ? 'elder elder-large' : 'elder'}>
      <header className="elder-head">
        <h1>
          {elder ? 'Your voice' : 'Preserve a memory'}{' '}
          <span className="yo" lang="yo">
            {elder ? 'Ohùn rẹ' : 'Pa á mọ́'}
          </span>
        </h1>
        <p>{elder ? 'Press a big button. Speak. That is all — we keep the rest.' : 'One tap, one recording. Add the words and the details whenever you like.'}</p>
      </header>

      <Notice text={act.notice} />
      <ErrorBox error={act.error} />
      {saved && (
        <p className="elder-saved" role="status">
          ✅ Kept for your family — {saved}
        </p>
      )}

      {!kind ? (
        <div className="elder-grid">
          {KINDS.map((k) => (
            <button key={k.key} className="elder-btn" onClick={() => setKind(k.key)}>
              <span className="elder-icon" aria-hidden="true">
                {k.icon}
              </span>
              <span className="elder-label">{k.label}</span>
              {term(k.termKey) && (
                <span className="elder-yo yo" lang={primary?.code}>
                  {term(k.termKey)}
                </span>
              )}
            </button>
          ))}
        </div>
      ) : (
        <div className="elder-record">
          <p className="elder-prompt">{chosen?.prompt}</p>
          {busy ? (
            <Loading label="Keeping it safe…" />
          ) : (
            <Recorder onRecorded={save} maxSeconds={1800} label="Start speaking" />
          )}
          <button className="elder-back" onClick={() => setKind(null)} disabled={busy}>
            ← Something else
          </button>
        </div>
      )}

      <details className="elder-extra">
        <summary>Who is speaking, and how you speak it</summary>
        <div className="stack">
          <label className="elder-field">
            <span>Who is speaking</span>
            <select value={personId} onChange={(e) => setPersonId(e.target.value)}>
              <option value="">Not saying</option>
              {(people.data?.people ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.relation_label ? `${p.relation_label} ${p.display_name}` : p.display_name}
                </option>
              ))}
            </select>
          </label>
          <label className="elder-field">
            <span>How your side speaks Yorùbá</span>
            <select value={dialectCode} onChange={(e) => setDialectCode(e.target.value)}>
              <option value="">Not saying</option>
              {(dialects.data?.dialects ?? []).map((d) => (
                <option key={d.code} value={d.code}>
                  {d.name} — {d.english_name}
                  {d.region ? ` (${d.region})` : ''}
                </option>
              ))}
            </select>
          </label>
          <p className="muted small">
            Your way of speaking is not a mistake to be corrected. Naming it lets your grandchildren learn the Yorùbá you actually speak, not only the one taught in
            school.
          </p>
        </div>
      </details>
    </div>
  );
}
